import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  Document, Packer, Paragraph, Table, TableRow, TableCell,
  TextRun, HeadingLevel, AlignmentType, WidthType, BorderStyle,
} from 'docx';
import { saveAs } from 'file-saver';
import '../styles/AnnualConferenceReport.css';

// ── Constants ─────────────────────────────────────────────────────────────────

const COLS = [
  { key: 'lastName',            label: 'Last Name' },
  { key: 'firstName',           label: 'First Name' },
  { key: 'middleName',          label: 'Middle Name' },
  { key: 'district',            label: 'District' },
  { key: 'firstYearLicense',    label: 'First Year License Awarded' },
  { key: 'cosEducation',        label: 'Course of Study / Education' },
  { key: 'lastRenewalDate',     label: 'Last Renewal Date' },
  { key: 'serviceStatus',       label: 'Full-Time / Part-Time' },
];

const PROCESS_STAGE_LABEL = {
  'full-time':     'Full-Time',
  'part-time':     'Part-Time',
  'retired':       'Retired',
  'active-retired':'Retired (Active)',
  'archived':      'Archived',
  'discontinued':  'Discontinued',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const serviceLabel = (stage) => PROCESS_STAGE_LABEL[stage] || (stage ? stage.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—');

const cosEducation = (candidate) => {
  // Seminary info takes priority
  if (candidate.education && candidate.education.trim()) return candidate.education.trim();
  const cos = candidate.courseOfStudy || {};
  const completed = cos.completedCourses ?? (cos.courses || []).filter(c => c.status === 'completed').length;
  const total = cos.totalCourses ?? (cos.courses || []).length;
  if (total > 0) {
    const pct = Math.round((completed / total) * 100);
    return `${completed} of ${total} courses (${pct}%)`;
  }
  return '—';
};

const localToday = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const formatDate = (iso) => {
  if (!iso || iso === '—') return '—';
  // Strip any time component (e.g. "2026-03-21T05:00:00.000Z" → "2026-03-21")
  const datePart = String(iso).split('T')[0];
  const parts = datePart.split('-');
  if (parts.length !== 3) return String(iso);
  const [y, m, d] = parts;
  return `${m}/${d}/${y}`;
};

const escapeCSV = (val) => {
  if (val == null) return '';
  const s = String(val).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
};

// ── CSV export ────────────────────────────────────────────────────────────────

const exportCSV = (llpRows, rlpRows, startDate, endDate, includeLLP, includeRLP) => {
  const lines = [`Business of the Annual Conference – Local Pastors`];
  lines.push(`Reporting Period: ${startDate} to ${endDate}`);
  lines.push('');

  const header = COLS.map(c => c.label).join(',');

  if (includeLLP && llpRows.length > 0) {
    lines.push('LICENSED LOCAL PASTORS');
    lines.push(header);
    llpRows.forEach(r => lines.push(COLS.map(c => escapeCSV(r[c.key])).join(',')));
    lines.push('');
  }

  if (includeRLP && rlpRows.length > 0) {
    lines.push('RETIRED LOCAL PASTORS');
    lines.push(header);
    rlpRows.forEach(r => lines.push(COLS.map(c => escapeCSV(r[c.key])).join(',')));
  }

  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `annual-conference-report-${startDate}-${endDate}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ── DOCX export ───────────────────────────────────────────────────────────────

const noBorder = {
  top:    { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
  bottom: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
  left:   { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
  right:  { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
};

const makeDocxTable = (rows) => {
  const headerRow = new TableRow({
    tableHeader: true,
    children: COLS.map(c =>
      new TableCell({
        borders: noBorder,
        shading: { fill: 'E8EDF2' },
        children: [new Paragraph({
          children: [new TextRun({ text: c.label, bold: true, size: 18 })],
        })],
      })
    ),
  });

  const dataRows = rows.map(r =>
    new TableRow({
      children: COLS.map(c =>
        new TableCell({
          borders: noBorder,
          children: [new Paragraph({
            children: [new TextRun({ text: String(r[c.key] ?? ''), size: 18 })],
          })],
        })
      ),
    })
  );

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [headerRow, ...dataRows],
  });
};

const exportDOCX = async (llpRows, rlpRows, startDate, endDate, includeLLP, includeRLP) => {
  const children = [
    new Paragraph({
      text: 'Business of the Annual Conference – Local Pastors',
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({
      children: [new TextRun({ text: `Reporting Period: ${startDate} to ${endDate}`, size: 22, color: '555555' })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
    }),
  ];

  if (includeLLP && llpRows.length > 0) {
    children.push(
      new Paragraph({ text: 'Licensed Local Pastors', heading: HeadingLevel.HEADING_1, spacing: { before: 200, after: 200 } }),
      makeDocxTable(llpRows),
      new Paragraph({ text: '', spacing: { after: 400 } }),
    );
  }

  if (includeRLP && rlpRows.length > 0) {
    children.push(
      new Paragraph({ text: 'Retired Local Pastors', heading: HeadingLevel.HEADING_1, spacing: { before: 200, after: 200 } }),
      makeDocxTable(rlpRows),
    );
  }

  const doc = new Document({
    styles: {
      default: {
        document: { run: { font: 'Calibri', size: 20 } },
      },
    },
    sections: [{ children }],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `annual-conference-report-${startDate}-${endDate}.docx`);
};

// ── Component ─────────────────────────────────────────────────────────────────

const AnnualConferenceReport = () => {
  const { districts } = useAuth();

  const [candidates, setCandidates]     = useState([]);
  const [actionReportMap, setActionReportMap] = useState({}); // candidateId → sorted reports[]
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [exporting, setExporting]       = useState(false);

  // Filters
  const today = localToday();
  const prevJul1 = `${new Date().getFullYear() - 1}-07-01`;
  const [startDate, setStartDate]       = useState(prevJul1);
  const [endDate, setEndDate]           = useState(today);
  const [includeLLP, setIncludeLLP]     = useState(true);
  const [includeRLP, setIncludeRLP]     = useState(true);
  const [apptOnly, setApptOnly]         = useState(false);   // retired: under appointment only

  // ── Fetch ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [candSnap, arSnap] = await Promise.all([
          getDocs(collection(db, 'candidates')),
          getDocs(query(collection(db, 'actionReports'), orderBy('date', 'asc'))),
        ]);

        // Build candidateId → [{date, actionItems}] map
        const arMap = {};
        arSnap.docs.forEach(d => {
          const ar = d.data();
          if (!arMap[ar.candidateId]) arMap[ar.candidateId] = [];
          arMap[ar.candidateId].push({ date: ar.date, actionItems: ar.actionItems || {} });
        });

        setCandidates(
          candSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(c => !c.deleted)
        );
        setActionReportMap(arMap);
      } catch (err) {
        console.error('Error loading annual conference report data:', err);
        setError('Failed to load report data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // ── Build row ─────────────────────────────────────────────────────────────
  const buildRow = (candidate) => {
    const reports = actionReportMap[candidate.id] || [];

    // First year license: stored field takes priority; fall back to action report history
    let firstYearLicense = candidate.firstYearLicense
      ? String(candidate.firstYearLicense)
      : null;
    if (!firstYearLicense) {
      const firstLicenseReport = reports.find(r =>
        (r.actionItems?.certificationStatus || []).includes('recommend-first-licensing')
      );
      firstYearLicense = firstLicenseReport
        ? firstLicenseReport.date.slice(0, 4)
        : reports.length > 0
          ? reports[0].date.slice(0, 4)
          : '—';
    }

    // Last renewal: most recent action report within reporting period
    const inPeriod = reports.filter(r => r.date >= (startDate || '') && r.date <= (endDate || '9999'));
    const lastRenewalDate = inPeriod.length > 0 ? inPeriod[inPeriod.length - 1].date : '—';

    return {
      lastName:         candidate.lastName  || '',
      firstName:        candidate.firstName || '',
      middleName:       candidate.middleName || '',
      district:         candidate.district  || '',
      firstYearLicense,
      cosEducation:     cosEducation(candidate),
      lastRenewalDate: formatDate(lastRenewalDate),
      serviceStatus:    serviceLabel(candidate.processStage),
      currentAppointment: candidate.currentAppointment || '',
    };
  };

  // ── Derived rows ──────────────────────────────────────────────────────────
  const { llpRows, rlpRows } = useMemo(() => {
    const llp = candidates
      .filter(c => c.conferenceStatus === 'local-pastor')
      .map(buildRow)
      .sort((a, b) => a.district.localeCompare(b.district) || a.lastName.localeCompare(b.lastName));

    const rlp = candidates
      .filter(c => {
        if (c.conferenceStatus !== 'retired-local') return false;
        if (apptOnly && !(c.currentAppointment || '').trim()) return false;
        return true;
      })
      .map(buildRow)
      .sort((a, b) => a.district.localeCompare(b.district) || a.lastName.localeCompare(b.lastName));

    return { llpRows: llp, rlpRows: rlp };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates, actionReportMap, startDate, endDate, apptOnly]);

  // ── Exports ───────────────────────────────────────────────────────────────
  const handleCSV = () => exportCSV(llpRows, rlpRows, startDate, endDate, includeLLP, includeRLP);

  const handleDOCX = async () => {
    setExporting(true);
    try {
      await exportDOCX(llpRows, rlpRows, startDate, endDate, includeLLP, includeRLP);
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => window.print();

  // ── Render helpers ────────────────────────────────────────────────────────
  const SectionTable = ({ rows, title }) => {
    if (rows.length === 0) return (
      <div className="acr-section">
        <h2 className="acr-section-title">{title}</h2>
        <p className="acr-empty">No records match the current filters.</p>
      </div>
    );

    return (
      <div className="acr-section">
        <h2 className="acr-section-title">{title} <span className="acr-count">({rows.length})</span></h2>
        <div className="acr-table-wrapper">
          <table className="acr-table">
            <thead>
              <tr>
                {COLS.map(c => <th key={c.key}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className={i % 2 === 0 ? 'acr-row-even' : ''}>
                  <td className="acr-bold">{r.lastName}</td>
                  <td>{r.firstName}</td>
                  <td>{r.middleName || <span className="acr-dim">—</span>}</td>
                  <td>{r.district}</td>
                  <td>{r.firstYearLicense}</td>
                  <td className="acr-cos">{r.cosEducation}</td>
                  <td>{r.lastRenewalDate}</td>
                  <td>
                    <span className={`acr-service acr-service-${(r.serviceStatus || '').toLowerCase().replace(/[^a-z]/g, '-')}`}>
                      {r.serviceStatus}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return <div className="acr-container"><div className="acr-loading">Loading report data…</div></div>;
  if (error)   return <div className="acr-container"><div className="acr-error">{error}</div></div>;

  return (
    <div className="acr-container">

      {/* ── Header ── */}
      <div className="acr-header no-print">
        <div className="acr-header-text">
          <h1>Business of the Annual Conference</h1>
          <p>Licensed &amp; Retired Local Pastors</p>
        </div>
        <div className="acr-header-actions">
          <button className="acr-btn acr-btn-outline" onClick={handlePrint}>Print / PDF</button>
          <button className="acr-btn acr-btn-secondary" onClick={handleCSV}>Export CSV</button>
          <button className="acr-btn acr-btn-primary" onClick={handleDOCX} disabled={exporting}>
            {exporting ? 'Generating…' : 'Export DOCX'}
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="acr-filters no-print">
        <div className="acr-filter-group">
          <label>Reporting Period</label>
          <div className="acr-date-range">
            <input type="date" value={startDate} max={endDate} onChange={e => setStartDate(e.target.value)} />
            <span className="acr-sep">to</span>
            <input type="date" value={endDate} min={startDate} max={today} onChange={e => setEndDate(e.target.value)} />
          </div>
        </div>

        <div className="acr-filter-group">
          <label>Include</label>
          <div className="acr-checkboxes">
            <label className="acr-check">
              <input type="checkbox" checked={includeLLP} onChange={e => setIncludeLLP(e.target.checked)} />
              Licensed Local Pastors
            </label>
            <label className="acr-check">
              <input type="checkbox" checked={includeRLP} onChange={e => setIncludeRLP(e.target.checked)} />
              Retired Local Pastors
            </label>
          </div>
        </div>

        {includeRLP && (
          <div className="acr-filter-group">
            <label>Retired Options</label>
            <label className="acr-check">
              <input type="checkbox" checked={apptOnly} onChange={e => setApptOnly(e.target.checked)} />
              Under appointment only
            </label>
          </div>
        )}
      </div>

      {/* ── Summary ── */}
      <div className="acr-summary no-print">
        {includeLLP && <span><strong>{llpRows.length}</strong> Licensed Local Pastors</span>}
        {includeLLP && includeRLP && <span className="acr-dot">·</span>}
        {includeRLP && <span><strong>{rlpRows.length}</strong> Retired Local Pastors{apptOnly ? ' (under appointment)' : ''}</span>}
        <span className="acr-period">Period: {startDate} – {endDate}</span>
      </div>

      {/* ── Print header ── */}
      <div className="print-only acr-print-header">
        <h2>Business of the Annual Conference – Local Pastors</h2>
        <p>Reporting Period: {startDate} to {endDate}</p>
        {apptOnly && includeRLP && <p>Retired: Under Appointment Only</p>}
        <p className="acr-print-date">Generated: {new Date().toLocaleDateString()}</p>
      </div>

      {/* ── Tables ── */}
      {includeLLP && <SectionTable rows={llpRows} title="Licensed Local Pastors" />}
      {includeRLP && <SectionTable rows={rlpRows} title="Retired Local Pastors" />}

    </div>
  );
};

export default AnnualConferenceReport;
